/**
 * Typed Socket.IO client for the dashboard realtime channel.
 *
 * Connects to the backend's `/v1/realtime` namespace with the JWT in the
 * handshake (`auth: { token }`, matching the gateway's `extractHandshakeToken`).
 * The server joins the socket to its `org:<orgId>` room and pushes
 * `feedback.created` / `feedback.updated` / `comment.created`. Event names +
 * payload types come straight from `@hotline/contracts` so client/server can't
 * drift.
 *
 * `socket.io-client`'s factory is injectable so unit tests can drive a fake
 * socket without a network.
 */
import {
  RealtimeEvent,
  type CommentCreatedPayload,
  type FeedbackCreatedPayload,
  type FeedbackUpdatedPayload,
  type ServerToClientEvents,
} from '@hotline/contracts';
import { io, type Socket } from 'socket.io-client';

/** Namespace the dashboard connects to (mirrors the backend constant). */
export const REALTIME_NAMESPACE = '/v1/realtime';

/** Minimal socket surface we rely on (so tests can inject a fake). */
export interface RealtimeSocketLike {
  connected: boolean;
  on(event: string, handler: (...args: unknown[]) => void): unknown;
  off(event: string, handler?: (...args: unknown[]) => void): unknown;
  emit(event: string, ...args: unknown[]): unknown;
  connect(): unknown;
  disconnect(): unknown;
}

/** Factory matching `io(url, opts)` from socket.io-client, narrowed + injectable. */
export type SocketFactory = (
  url: string,
  opts: { auth?: { token?: string }; autoConnect?: boolean; transports?: string[] },
) => RealtimeSocketLike;

export interface RealtimeClientOptions {
  /** Base URL of the backend (the namespace path is appended). */
  baseUrl: string;
  /** Returns the current access token at connect time (re-read on reconnect). */
  getToken: () => string | null;
  /** Injected socket factory (defaults to socket.io-client `io`). */
  socketFactory?: SocketFactory;
}

/** Strongly-typed handler map for the three server→client events. */
export interface RealtimeHandlers {
  'feedback.created': (payload: FeedbackCreatedPayload) => void;
  'feedback.updated': (payload: FeedbackUpdatedPayload) => void;
  'comment.created': (payload: CommentCreatedPayload) => void;
}

/** Connection lifecycle callback. */
export type ConnectionListener = (connected: boolean) => void;

const defaultFactory: SocketFactory = (url, opts) =>
  io(url, opts) as unknown as RealtimeSocketLike;

/**
 * A thin, typed wrapper over a Socket.IO connection to the dashboard channel.
 * Subscriptions are registered via `on(event, handler)` and return an
 * unsubscribe function; `connect()`/`disconnect()` manage the underlying socket.
 */
export class RealtimeClient {
  private readonly url: string;
  private readonly getToken: () => string | null;
  private readonly factory: SocketFactory;
  private socket: RealtimeSocketLike | null = null;
  /**
   * The token presented on the current socket's handshake. Socket.IO captures
   * `auth` when the socket is constructed, so a rotated token can only reach the
   * server via a brand-new socket — this is what `connect()` compares against to
   * decide whether the existing one is still usable.
   */
  private handshakeToken: string | undefined;

  private readonly handlers: {
    [K in keyof RealtimeHandlers]: Set<RealtimeHandlers[K]>;
  } = {
    [RealtimeEvent.FeedbackCreated]: new Set(),
    [RealtimeEvent.FeedbackUpdated]: new Set(),
    [RealtimeEvent.CommentCreated]: new Set(),
  };
  private readonly connectionListeners = new Set<ConnectionListener>();

  constructor(options: RealtimeClientOptions) {
    this.url = `${options.baseUrl.replace(/\/+$/, '')}${REALTIME_NAMESPACE}`;
    this.getToken = options.getToken;
    this.factory = options.socketFactory ?? defaultFactory;
  }

  get connected(): boolean {
    return this.socket?.connected ?? false;
  }

  /**
   * Open the connection (idempotent). Re-reads the token on every call: if it
   * changed since the current socket handshook — a 401 refresh rotated it, or a
   * logout cleared it — that socket would go on authenticating as the previous
   * principal, so it is torn down and replaced rather than merely reconnected.
   */
  connect(): void {
    const token = this.getToken() ?? undefined;
    if (this.socket) {
      if (token === this.handshakeToken) {
        this.socket.connect();
        return;
      }
      this.disconnect();
    }
    this.handshakeToken = token;
    this.socket = this.factory(this.url, {
      auth: { token },
      autoConnect: true,
      transports: ['websocket', 'polling'],
    });

    this.socket.on('connect', () => this.emitConnection(true));
    this.socket.on('disconnect', () => this.emitConnection(false));

    // Wire each contract event to its handler-set fan-out.
    for (const event of Object.values(RealtimeEvent)) {
      this.socket.on(event, (payload: unknown) => {
        const set = this.handlers[event as keyof RealtimeHandlers];
        for (const fn of set) {
          (fn as (p: unknown) => void)(payload);
        }
      });
    }
  }

  /** Close the connection and forget the socket (and the token it presented). */
  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.handshakeToken = undefined;
    }
  }

  /**
   * Subscribe to a typed realtime event. Returns an unsubscribe function.
   * Safe to call before `connect()`.
   */
  on<K extends keyof RealtimeHandlers>(
    event: K,
    handler: RealtimeHandlers[K],
  ): () => void {
    this.handlers[event].add(handler);
    return () => {
      this.handlers[event].delete(handler);
    };
  }

  /** Subscribe to connection up/down transitions. Returns an unsubscribe fn. */
  onConnectionChange(listener: ConnectionListener): () => void {
    this.connectionListeners.add(listener);
    return () => {
      this.connectionListeners.delete(listener);
    };
  }

  private emitConnection(connected: boolean): void {
    for (const fn of this.connectionListeners) fn(connected);
  }
}

/**
 * Compile-time assurance the handler map stays aligned with the contract's
 * `ServerToClientEvents`. Purely structural; erased at build.
 */
type _AssertEventsAligned = RealtimeHandlers extends ServerToClientEvents
  ? true
  : never;
const _eventsAligned: _AssertEventsAligned = true;
void _eventsAligned;

export type { Socket };
